<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { reportError } from '@/errorReport'
import { useTodosStore } from '@/stores/todos'
import { hapticSuccess, showBackButton } from '@/telegramWebApp'
import { parseNewTodo, targetCalendar } from '@/todos/newTodo'
import { isTagOnly } from '@/todos/hashtags'
import { tagFromParam } from '@/todos/listView'
import ChipEditor from './ChipEditor.vue'

// A round "+" button on every page that opens a big text box: the first line becomes the title of a new todo,
// the rest its body. It goes to the list on the screen, else to the default list. The box is a multi-line `ChipEditor`,
// so a hashtag typed in it is completed and picked as a tag chip like in the quick add field.
const { t } = useI18n()
const route = useRoute()
const store = useTodosStore()
const toast = useToast()

const open = ref(false)
const text = ref('')
const saving = ref(false)
const calendarId = ref<number | null>(null)
const editor = ref<InstanceType<typeof ChipEditor> | null>(null)

const writable = computed(() => store.calendars.filter((calendar) => !calendar.readonly))
const parsed = computed(() => parseNewTodo(text.value))
// On a tag page the box starts with that tag as a chip, and with only that in it there is nothing to add.
const pageTag = computed(() => tagFromParam(route.params.tag))
const canAdd = computed(
  () => !!parsed.value && !(pageTag.value && isTagOnly(text.value, pageTag.value)),
)

// While the box is open, Telegram's back button (and the desktop client's Esc, which is mapped to it) closes the box
// instead of running the page's navigation below it.
let hideBackButton: (() => void) | null = null
watch(open, (isOpen) => {
  hideBackButton?.()
  hideBackButton = isOpen ? showBackButton(() => (open.value = false)) : null
})
onBeforeUnmount(() => hideBackButton?.())

function currentCalendarId(): number | null {
  const id = Number(route.params.calendarId)
  return Number.isInteger(id) && id > 0 ? id : null
}

async function show(): Promise<void> {
  text.value = ''
  open.value = true
  try {
    if (!store.calendarsLoaded) await store.loadCalendars()
  } catch (e) {
    reportError(e, 'list.err.add')
    toast.error(e, t('list.err.add'))
  }
  calendarId.value = targetCalendar(store.calendars, currentCalendarId())?.id ?? null
  editor.value?.focus()
}

async function submit(): Promise<void> {
  const todo = parsed.value
  const id = calendarId.value
  if (!todo || !canAdd.value || id === null || saving.value) return
  saving.value = true
  try {
    const fields: { summary: string; description?: string } = { summary: todo.summary }
    if (todo.description) fields.description = todo.description
    await store.create(id, fields)
    hapticSuccess()
    toast.info(t('fab.added', { list: store.calendarById(id)?.name ?? '' }))
    text.value = ''
    open.value = false
  } catch (e) {
    reportError(e, 'list.err.add')
    toast.error(e, t('list.err.add'))
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <button v-if="!open" type="button" class="fab" :aria-label="t('fab.add')" @click="show">
    ＋
  </button>
  <div v-else class="ui-backdrop" @click.self="open = false">
    <form
      class="ui-sheet sheet"
      role="dialog"
      aria-modal="true"
      @submit.prevent="submit"
      @keydown.esc.prevent="open = false"
    >
      <label v-if="writable.length > 1" class="list-pick">
        <span class="ui-hint">{{ t('fab.list') }}</span>
        <select v-model="calendarId" class="ui-select">
          <option v-for="calendar in writable" :key="calendar.id" :value="calendar.id">
            {{ calendar.name }}
          </option>
        </select>
      </label>
      <ChipEditor
        ref="editor"
        v-model:plain="text"
        class="field"
        multiline
        autofocus
        :placeholder="t('fab.placeholder')"
        :disabled="saving"
        :prefill-tag="pageTag"
        @submit="submit"
      />
      <div class="actions">
        <button type="button" class="ui-btn" @click="open = false">{{ t('common.cancel') }}</button>
        <button
          type="submit"
          class="ui-btn ui-btn-primary"
          :disabled="!canAdd || calendarId === null || saving"
        >
          {{ t('common.add') }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.fab {
  position: fixed;
  right: 1rem;
  bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  z-index: 15;
  width: 3.4rem;
  height: 3.4rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--tg-button-color);
  color: var(--tg-button-text-color);
  font-size: 1.9rem;
  line-height: 1;
  box-shadow: 0 2px 10px rgb(0 0 0 / 0.35);
  cursor: pointer;
}

.sheet {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-height: 60vh;
}

.list-pick {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.field {
  flex: 1;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
